import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Database, FileJson, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { SortableTH, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/data/EmptyState';
import { TimeframeSelect } from '@/components/ui/TimeframeSelect';
import { HarvestSyncChart } from '@/components/charts/HarvestSyncChart';
import { useToast } from '@/components/ui/toast';
import { findEntity, findProvider } from '@/config/providers';
import { DEFAULT_TIMEFRAME, type TimeframeKey } from '@/config/timeframes';
import { useProviderEntity, useEntityFreshness } from '@/hooks/useProviderEntities';
import { useEntityHarvestVsSync } from '@/hooks/useEntityHarvestVsSync';
import type { SortDir } from '@/lib/useTableSort';
import { absoluteStamp, formatNumber, formatRelative } from '@/lib/utils';

interface EntitySort {
  column: string;
  dir: SortDir;
}

export function EntityRoute() {
  // All hooks must run BEFORE any conditional return so the call order is
  // stable across renders (Rules of Hooks). useProviderEntity is gated on
  // (provider && entity) internally so no PostgREST call fires for an
  // unknown URL slug.
  const toast = useToast();
  const { providerKey = '', entityKey = '' } = useParams();
  const provider = findProvider(providerKey);
  const entity = provider ? findEntity(provider, entityKey) : null;
  const [sport, setSport] = useState(provider?.sports[0] ?? '');
  const [search, setSearch] = useState('');
  const [timeframe, setTimeframe] = useState<TimeframeKey>(DEFAULT_TIMEFRAME);
  const [inspect, setInspect] = useState<Record<string, unknown> | null>(null);
  // Server-side sort state for the datagrid. Initialized from the entity's
  // default `orderBy` (column + direction) so the initial view is unchanged;
  // null = no explicit sort, which lets the hook fall back to `entity.orderBy`.
  const [sort, setSort] = useState<EntitySort | null>(() => initialSort(entity));
  // React Router reuses this component instance when navigating between
  // entity routes (e.g. /entities/genius/leagues → /entities/presto/rpi),
  // so `sport` and `search` would carry over and could filter the new
  // provider/entity by a sport that doesn't exist there. Reset both
  // whenever the URL slug changes.
  useEffect(() => {
    setSport(provider?.sports[0] ?? '');
    setSearch('');
    // Also dismiss any open row-inspector dialog so a previous entity's
    // row payload doesn't stay visible after navigation.
    setInspect(null);
    // Reset the column sort to the new entity's default ordering.
    setSort(initialSort(entity));
  }, [providerKey, entityKey, provider, entity]);

  // Cycle a header: asc -> desc -> back to the entity default (null). When the
  // clicked column differs from the active one, start ascending.
  function toggleSort(column: string): void {
    setSort((prev) => {
      if (!prev || prev.column !== column) return { column, dir: 'asc' };
      if (prev.dir === 'asc') return { column, dir: 'desc' };
      return initialSort(entity);
    });
  }

  const query = useProviderEntity(provider, entity, {
    sport: sport || undefined,
    search: search || undefined,
    limit: 200,
    sortColumn: sort?.dir ? sort.column : undefined,
    sortAscending: sort?.dir === 'asc',
  });
  const series = useEntityHarvestVsSync(provider, entity, timeframe);
  const freshness = useEntityFreshness(provider, entity);

  if (!provider) return <Navigate to="/" replace />;
  if (!entity) return <Navigate to={`/entities/${providerKey}`} replace />;

  return (
    <>
      <PageHeader
        title={`${provider.label} · ${entity.label}`}
        description={
          entity.description ?? `Rows from ${provider.schema}.${entity.table}, sport-filtered.`
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/entities/${provider.key}`} className="text-xs text-muted-foreground hover:underline">
              ← All {provider.label} entities
            </Link>
          </div>
        }
      />

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:space-y-0">
          <div>
            <CardTitle>Harvest vs sync</CardTitle>
            <CardDescription>
              Items harvested (S3/manifest) vs synced (rows written to{' '}
              {provider.schema}.{entity.table}) per bucket. Synced is derived from{' '}
              <code>updated_at</code>, so it counts all DB writes (sync, backfill, enrichment).
            </CardDescription>
          </div>
          <div className="flex flex-col items-start gap-2 md:items-end">
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span title={`Last harvested: ${absoluteStamp(freshness.data?.lastHarvested)}`}>
                Last harvested{' '}
                {freshness.isPending ? (
                  <Skeleton className="inline-block h-3 w-12 align-middle" />
                ) : (
                  <span className="text-foreground/80">{formatRelative(freshness.data?.lastHarvested)}</span>
                )}
              </span>
              <span className="text-border">·</span>
              <span title={`Last synced: ${absoluteStamp(freshness.data?.lastSynced)}`}>
                Last synced{' '}
                {freshness.isPending ? (
                  <Skeleton className="inline-block h-3 w-12 align-middle" />
                ) : (
                  <span className="text-foreground/80">{formatRelative(freshness.data?.lastSynced)}</span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <TimeframeSelect value={timeframe} onChange={setTimeframe} />
              <Button variant="outline" size="sm" onClick={() => void series.refetch()}>
                <RefreshCw className="h-4 w-4" /> Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {series.isError ? (
            <ErrorState error={series.error} />
          ) : series.isLoading ? (
            <Skeleton className="h-56 w-full" />
          ) : (
            <HarvestSyncChart data={series.data?.points ?? []} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" />
              {provider.schema}.{entity.table}
            </CardTitle>
            <CardDescription>
              {query.data?.total != null
                ? `${formatNumber(query.data.total)} total rows · showing first 200`
                : 'Loading row count…'}
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={sport} onChange={(e) => setSport(e.target.value)} className="w-36">
              <option value="">All sports</option>
              {provider.sports.map((s) => (
                <option key={s} value={s} className="capitalize">{s}</option>
              ))}
            </Select>
            <Input
              placeholder="Search names…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-56"
            />
          </div>
        </CardHeader>
        <CardContent>
          {query.isError ? (
            <ErrorState error={query.error} />
          ) : query.isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : query.data?.rows.length ? (
            <Table>
              <THead>
                <TR>
                  {entity.columns.map((c) => (
                    <SortableTH
                      key={c.key}
                      label={c.label}
                      sortKey={c.key}
                      active={sort?.column === c.key && sort?.dir != null}
                      direction={sort?.column === c.key ? sort.dir : null}
                      onSort={toggleSort}
                    />
                  ))}
                  <TH className="text-right">Row</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.rows.map((row, idx) => (
                  <TR key={String(row.id ?? idx)}>
                    {entity.columns.map((c) => (
                      <TD key={c.key} className={c.mono ? 'font-mono text-xs' : ''}>
                        {renderCell(row[c.key], c.format)}
                      </TD>
                    ))}
                    <TD className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setInspect(row)}>
                        <FileJson className="h-3 w-3" /> JSON
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          ) : (
            <EmptyState
              icon={Database}
              title="No rows match the current filters"
              description="Try clearing the sport filter or adjusting the search."
            />
          )}
        </CardContent>
      </Card>

      {inspect && (
        <Dialog
          open
          onClose={() => setInspect(null)}
          title="Row inspector"
          description="Full row payload from PostgREST."
          className="max-w-2xl"
          footer={
            <Button
              variant="outline"
              onClick={() => {
                const payload = JSON.stringify(inspect, null, 2);
                const p = navigator.clipboard?.writeText(payload);
                if (p) {
                  p.then(
                    () => toast.push('info', 'Copied row JSON to clipboard'),
                    (e) =>
                      toast.push(
                        'error',
                        `Copy failed: ${e instanceof Error ? e.message : String(e)}`,
                      ),
                  );
                } else {
                  toast.push('error', 'Clipboard API unavailable in this context');
                }
              }}
            >
              Copy JSON
            </Button>
          }
        >
          <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-background p-3 font-mono text-[11px] leading-relaxed scrollbar-thin">
            {JSON.stringify(inspect, null, 2)}
          </pre>
        </Dialog>
      )}
    </>
  );
}

/** Derive the datagrid's initial sort from the entity's default `orderBy`. */
function initialSort(entity: { orderBy?: { column: string; ascending: boolean } } | null | undefined): EntitySort | null {
  if (!entity?.orderBy) return null;
  return { column: entity.orderBy.column, dir: entity.orderBy.ascending ? 'asc' : 'desc' };
}

function renderCell(value: unknown, format?: (v: unknown) => string): React.ReactNode {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  if (format) return format(value);
  if (typeof value === 'boolean') return <Badge variant={value ? 'success' : 'outline'}>{String(value)}</Badge>;
  if (typeof value === 'object') return <span className="font-mono text-[11px]">{JSON.stringify(value).slice(0, 60)}</span>;
  return String(value);
}
